/**
 * The checkpoint (spec 9.5, prototype B "Stapel"): one screen, the blurred last-card image
 * behind, five segments across the top, and a deck whose front card is the current question
 * with the next two peeking behind it.
 *
 * Timing is the prototype's: 900 ms of feedback, then the card slides out left with a slight
 * rotation over 350 ms and the next comes forward. Nothing is tappable between questions.
 *
 * Every tap is awaited (build spec 4.3) — the API decides right or wrong and owns the score
 * (spec 7); the client only paints what comes back. A failed request unlocks the options again
 * with «Keine Verbindung. Nochmal tippen.» rather than guessing.
 */

import { useEffect, useRef, useState } from "react";
import type { Chapter, Question, StoredAnswer } from "../../shared/api-types.js";
import { Button } from "../../shared/primitives/Button.js";
import { GoalRing, TopicLabel } from "./CardChrome.js";

const FEEDBACK_MS = 900;
const SLIDE_OUT_MS = 350;

/** What the deck paints for a question once it has been answered. */
interface Graded {
  chosenOptionId: string;
  correctOptionId: string;
  isCorrect: boolean;
}

function gradedFromStored(answers: StoredAnswer[]): Map<string, Graded> {
  return new Map(
    answers.map((answer) => [
      answer.questionId,
      {
        chosenOptionId: answer.optionId,
        correctOptionId: answer.correctOptionId,
        isCorrect: answer.isCorrect,
      },
    ]),
  );
}

export function CheckpointScreen({
  chapter,
  categoryLabel,
  today,
  dayGoal,
  onAnswer,
}: {
  chapter: Chapter;
  categoryLabel: string;
  today: number;
  dayGoal: number;
  /** Resolves with the grade; rejects on a network failure so the options unlock again. */
  onAnswer: (question: Question, optionId: string) => Promise<Graded>;
}) {
  const questions = chapter.checkpoint.questions;
  const stored = gradedFromStored(chapter.checkpoint.answers);

  // Spec 6.1: reopening mid-checkpoint resumes at the next unanswered question, with the
  // answered segments already filled.
  const firstUnanswered = Math.max(
    0,
    questions.findIndex((question) => !stored.has(question.id)),
  );
  const [index, setIndex] = useState(firstUnanswered);
  const [graded, setGraded] = useState<Graded | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const pendingTimers = timers.current;
    return () => {
      for (const timer of pendingTimers) clearTimeout(timer);
    };
  }, []);

  const current = questions[index];
  if (!current) return null;

  const cardImage = chapter.cards.find((card) => card.position === current.cardPosition)?.image.url;
  const backdrop = chapter.cards[chapter.cards.length - 1]?.image.url;

  async function handleAnswer(optionId: string) {
    if (pending || graded || !current) return;
    setPending(true);
    setError(null);
    try {
      const grade = await onAnswer(current, optionId);
      setGraded(grade);
      setPending(false);
      // Let the feedback sit, then slide the card out and bring the next one forward.
      timers.current.push(
        setTimeout(() => {
          setLeaving(true);
          timers.current.push(
            setTimeout(() => {
              setLeaving(false);
              setGraded(null);
              setIndex((previous) => previous + 1);
            }, SLIDE_OUT_MS),
          );
        }, FEEDBACK_MS),
      );
    } catch {
      setPending(false);
      setError("Keine Verbindung. Nochmal tippen.");
    }
  }

  return (
    <section className="feed-screen">
      {backdrop && (
        <img
          src={backdrop}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full scale-115 object-cover blur-[24px] brightness-[.3]"
        />
      )}
      <TopicLabel category={categoryLabel} title={chapter.topic.title} />
      <GoalRing today={today} dayGoal={dayGoal} />

      <div
        className="absolute inset-0 flex flex-col px-5 pt-[70px] pb-10"
        style={{ paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <div
          className="mb-[18px] flex gap-1.5"
          role="progressbar"
          aria-valuenow={index}
          aria-valuemin={0}
          aria-valuemax={questions.length}
          aria-label={"Frage " + (index + 1) + " von " + questions.length}
        >
          {questions.map((question, position) => (
            <i
              key={question.id}
              className={
                "h-[5px] flex-1 rounded-[3px] " +
                (position < index
                  ? "bg-[var(--color-accent)]"
                  : position === index
                    ? "bg-white"
                    : "bg-white/20")
              }
            />
          ))}
        </div>

        <div className="relative flex-1">
          {/* Back to front, so the DOM order matches the visual stacking. */}
          {[index + 2, index + 1, index]
            .filter((position) => position < questions.length)
            .map((position) => {
              const question = questions[position];
              if (!question) return null;
              const depth = position - index;
              const isFront = depth === 0;
              return (
                <QuestionCard
                  key={question.id}
                  question={question}
                  number={position + 1}
                  total={questions.length}
                  imageUrl={
                    chapter.cards.find((card) => card.position === question.cardPosition)?.image
                      .url ?? cardImage
                  }
                  depth={depth}
                  graded={isFront ? graded : null}
                  pending={isFront && pending}
                  leaving={isFront && leaving}
                  error={isFront ? error : null}
                  onAnswer={isFront ? handleAnswer : undefined}
                />
              );
            })}
        </div>
      </div>
    </section>
  );
}

function QuestionCard({
  question,
  number,
  total,
  imageUrl,
  depth,
  graded,
  pending,
  leaving,
  error,
  onAnswer,
}: {
  question: Question;
  number: number;
  total: number;
  imageUrl: string | undefined;
  depth: number;
  graded: Graded | null;
  pending: boolean;
  leaving: boolean;
  error: string | null;
  /** Undefined on the two cards peeking behind: only the front card is tappable. */
  onAnswer: ((optionId: string) => void) | undefined;
}) {
  /**
   * Spec 9.5: the next two questions peek out below the current one, so a child can see more
   * is coming. `origin-bottom` is load-bearing. With the default centre origin, scaling a
   * 667 px-tall card to .95 lifts its bottom edge ~17 px, which is more than the 14 px it was
   * translated down by — the card ended up *above* the front one and the stack never stacked.
   * Scaling from the bottom edge instead means the peek equals the translate exactly, on any
   * screen height: 12 px and 24 px. Every card keeps that origin, so one coming forward does not
   * jump when its origin would otherwise switch to the centre.
   *
   * The cards behind are dimmed with `brightness`, never `opacity`: a see-through card let the
   * question behind it show through while it came forward, ghost text over the new question.
   */
  const depthClass =
    depth === 1
      ? "translate-y-[12px] scale-[.95] brightness-[.7]"
      : depth === 2
        ? "translate-y-[24px] scale-[.9] brightness-[.4]"
        : "";
  // Tailwind 4 moves cards with the separate `translate`, `rotate` and `scale` properties, not
  // `transform`, so those are what the transition below lists. It once listed `transform`: the
  // card never slid out, it faded where it stood, and the next one jumped to full size.
  const leavingClass = leaving ? "-translate-x-[120%] -rotate-[8deg] opacity-0" : "";
  /**
   * Nothing may spill out of a card. On a short screen (a phone with its browser toolbars
   * showing) a long question with three answers is taller than the card: the answers of the
   * card behind poked out below the front one, and the front card's own last answer slid off
   * the screen. The picture gives way first (it shrinks, see below); if even that is not
   * enough, the front card scrolls and the ones behind are clipped.
   */
  const overflowClass = depth === 0 ? "overflow-y-auto" : "overflow-hidden";
  const isTrueFalse = question.kind === "tf";

  return (
    <div
      className={
        "absolute inset-0 flex flex-col rounded-[28px] border border-white/8 bg-[var(--color-card)] px-[22px] py-[26px] shadow-[0_20px_50px_rgba(0,0,0,.6)] origin-bottom transition-[translate,rotate,scale,opacity,filter] duration-350 " +
        depthClass +
        " " +
        overflowClass +
        " " +
        leavingClass
      }
      aria-hidden={depth > 0}
      inert={depth > 0}
      data-own-scroll={depth === 0 ? "" : undefined}
    >
      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          className="mb-4 h-[140px] min-h-0 w-full rounded-[18px] bg-[#111] object-cover"
          loading={depth === 0 ? "eager" : "lazy"}
        />
      )}
      <div className="text-[12px] font-bold tracking-[.1em] text-[var(--color-accent)] uppercase">
        Frage {number} von {total}
      </div>
      <h2 className="mt-2.5 mb-0 text-[26px] leading-[1.15] font-extrabold text-white">
        {question.prompt}
      </h2>

      <div className={"mt-auto flex gap-2.5 " + (isTrueFalse ? "flex-row" : "flex-col")}>
        {question.options.map((option) => {
          const isCorrect = graded?.correctOptionId === option.id;
          const isWrongPick = graded ? graded.chosenOptionId === option.id && !isCorrect : false;
          const isDimmed = graded ? !isCorrect && !isWrongPick : false;
          const stateClass = isCorrect
            ? "bg-[var(--color-ok)] text-black"
            : isWrongPick
              ? "bg-[var(--color-bad)] text-white"
              : "bg-[var(--color-option)] text-white" + (isDimmed ? " opacity-35" : "");
          return (
            <Button
              key={option.id}
              onClick={() => onAnswer?.(option.id)}
              disabled={!onAnswer || pending || graded !== null}
              className={
                "w-full rounded-[22px] px-5 py-[18px] transition-colors duration-200 active:scale-[.98] " +
                (isTrueFalse
                  ? "flex-1 text-center text-[22px] font-extrabold"
                  : "text-left text-[18px] font-semibold") +
                " " +
                stateClass
              }
            >
              {option.text}
            </Button>
          );
        })}
      </div>

      <div className="mt-3.5 min-h-[22px] text-center text-[16px] font-bold">
        {error ? (
          <span className="text-[var(--color-bad)]">{error}</span>
        ) : graded ? (
          <span className={graded.isCorrect ? "text-[var(--color-ok)]" : "text-[var(--color-bad)]"}>
            {graded.isCorrect ? "Richtig!" : "Leider nein."}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export type { Graded };
